// WOS-BUNDLE: a person's recents on the server (Work OS domain/recents.ts, `/api/me/recents`):
// the last ten tasks, docs, boards and spaces they opened, newest first, only what they may still
// open. Opening one records it (`recents.record`), fire-and-forget: a failure is ignored and shows
// no toast. Work's sidebar "Recent" group and ⌘K's empty query read the list.
import { queryOptions, useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";
import type { Api } from "../../api/client";
import { useOperatorOnly } from "../operator-only";
import { pendingAsNull } from "./work-queries";

export type RecentView = NonNullable<
  Awaited<ReturnType<Api["client"]["recents"]["list"]>>
>["data"][number];
export type RecentKind = RecentView["kind"];

export const recentsKey = (co: string) => ["company", co, "work", "recents"] as const;

export const recentsQuery = (api: Api, co: string) =>
  queryOptions({
    queryKey: recentsKey(co),
    queryFn: async () =>
      (await pendingAsNull(() => api.client.recents.list({ params: { co } })))?.data ?? [],
    staleTime: 15_000,
  });

/** Notes an opening; the recents refetch after it. Errors (pending route, gone) are ignored. */
export function recordRecent(api: Api, co: string, kind: RecentKind, id: string) {
  return api.client.recents.record({ params: { co }, body: { kind, object_id: id } }).then(
    () => true,
    () => false,
  );
}

/** Records `id` once each time it becomes the one open (a task drawer, a doc, a board, a space). */
export function useRecordRecent(
  api: Api,
  co: string,
  kind: RecentKind,
  id: string | null | undefined,
) {
  const qc = useQueryClient();
  // OPERATOR-ONLY-UX: recents are a person member's; nothing is recorded for an operator-only
  // caller (the API would refuse it).
  const operatorOnly = useOperatorOnly(api, co);
  useEffect(() => {
    if (!id || operatorOnly) return;
    void recordRecent(api, co, kind, id).then((ok) => {
      if (ok) void qc.invalidateQueries({ queryKey: recentsKey(co) });
    });
  }, [api, co, kind, id, qc, operatorOnly]);
}
